import { useState } from 'react';
import {
  useGetMeetingsInfoQuery,
  useGetMeetingsQuery,
  useGetMeetingsDepartmentsOverviewQuery,
  useGetMyAssignmentsQuery,
  useSetMyAssignmentStatusMutation,
  useListMeetingSeriesQuery,
  useListPtoProjectsQuery,
} from '@shared/api';
import { useAgentPermission, useMeetingDeleteAccess } from '@entities/user';
import { FallbackBanner } from '@shared/ui/FallbackBanner';
import { Section, SectionLabel } from './styles';
import { UploadPanel } from './UploadPanel';
import { UploadProtocolPanel } from './UploadProtocolPanel';
import { MyAssignmentsSection } from './MyAssignmentsSection';
import { DepartmentsSection } from './DepartmentsSection';
import { MeetingsListSection } from './MeetingsListSection';
import { ModulesSection } from './ModulesSection';

/**
 * Обзор агента ОСНОВА.Совещания (корень `/agents/meetings`). Загрузка записи
 * (под правом записи), список последних совещаний со статусами и перечень модулей
 * из `/info`.
 */
export function MeetingsDashboardView() {
  const { can_read, can_write } = useAgentPermission('meetings');
  const { can_read: canReadPto } = useAgentPermission('pto');
  const canDelete = useMeetingDeleteAccess();
  const [showDeleted, setShowDeleted] = useState(false);
  const { data: info } = useGetMeetingsInfoQuery();
  const { data: list } = useGetMeetingsQuery(canDelete && showDeleted ? { deleted: true } : undefined, {
    pollingInterval: 5000,
  });
  const { data: deptOverview } = useGetMeetingsDepartmentsOverviewQuery();
  const { data: mine } = useGetMyAssignmentsQuery();
  const { data: seriesData } = useListMeetingSeriesQuery(undefined, { skip: !can_write });
  const { data: ptoProjectsData } = useListPtoProjectsQuery(undefined, {
    skip: !can_write || !canReadPto,
  });
  const [setMyStatus] = useSetMyAssignmentStatusMutation();

  if (!can_read) {
    return (
      <FallbackBanner tone="danger" title="Доступ запрещён">
        У вас нет прав на раздел «Совещания».
      </FallbackBanner>
    );
  }

  const modules = info?.modules ?? [];
  const meetings = list?.meetings ?? [];
  const departments = deptOverview?.departments ?? [];
  const myAssignments = mine?.assignments ?? [];
  const seriesList = seriesData?.series ?? [];
  const projects = ptoProjectsData?.items ?? [];

  return (
    <div>
      <MyAssignmentsSection
        assignments={myAssignments}
        onSetStatus={(id, status, meetingId) =>
          void setMyStatus({ id, status, meetingId })
        }
      />

      {can_write && (
        <Section>
          <SectionLabel>Новая запись</SectionLabel>
          <UploadPanel seriesList={seriesList} projects={projects} />
        </Section>
      )}

      {can_write && (
        <Section>
          <SectionLabel>Готовый протокол</SectionLabel>
          <UploadProtocolPanel seriesList={seriesList} projects={projects} />
        </Section>
      )}

      <DepartmentsSection departments={departments} />

      <MeetingsListSection
        meetings={meetings}
        canDelete={canDelete}
        showDeleted={showDeleted}
        onToggleDeleted={() => setShowDeleted((v) => !v)}
        can_write={can_write}
      />

      <ModulesSection modules={modules} />
    </div>
  );
}
